// Detección de plataforma Capacitor, en un solo lugar.
// En web, Capacitor.isNativePlatform() devuelve false (tiene fallback web),
// así que importar esto desde código compartido es seguro.
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'web' | 'android' | 'ios'

// ─── Plataforma para telemetría ───────────────────────────────────────────────
//
// POR QUÉ EXISTE (2026-09-04). `eventos_v2` tiene una columna `plataforma`
// desde mayo y nadie la escribía: 79.561 eventos, todos con NULL. El día que
// hizo falta fue el despliegue del TTL de caché y el recorte de conversación:
// los dos arreglos viven en el BUNDLE, así que la web y la PWA los reciben al
// abrir y la app de Play no, hasta que se publique una versión nueva. Sin este
// dato, medir el ahorro mezcla dos poblaciones que se comportan distinto y no
// hay forma de separarlas.
//
// Catálogo CERRADO de cuatro valores, PII-safe por construcción. La partición
// que importa no es "nativo o web" sino QUIÉN SE ACTUALIZA SOLO:
//   android · ios  → esperan una publicación en la tienda
//   pwa · web      → el service worker aplica la versión nueva al abrir
export const PLATAFORMAS = ['android', 'ios', 'pwa', 'web'];

/**
 * Clasifica la plataforma. Pura y testeable: recibe las señales, no las lee.
 *
 * EL ORDEN NO ES ARBITRARIO y está ganado con un caso real. Dentro de la app
 * NATIVA de iOS el WebView de Apple se ve igual que Safari y NO está en
 * display-mode standalone ni tiene navigator.standalone (ver el comentario de
 * lib/instalarPwa.js, que perdió una tester por esto el 2026-09-03). O sea la
 * app nativa cumple todas las señales de "navegador sin instalar". Por eso lo
 * nativo se pregunta PRIMERO: si se invierte, cada iPhone con la app instalada
 * se cuenta como web y la medición dice justo lo contrario de la verdad.
 */
export function clasificarPlataforma({
  nativo,
  plataformaNativa,
  displayModeStandalone,
  navigatorStandalone,
} = {}) {
  if (nativo) {
    // Capacitor solo devuelve 'android' o 'ios' cuando isNativePlatform() es
    // true. Si algún día devolviera otra cosa, no se inventa un valor: cae al
    // resto del árbol en vez de ensuciar el catálogo.
    if (plataformaNativa === 'android' || plataformaNativa === 'ios') return plataformaNativa;
  }
  // Instalada desde el navegador: Android y escritorio la marcan con
  // display-mode standalone; iOS Safari con navigator.standalone.
  if (displayModeStandalone === true || navigatorStandalone === true) return 'pwa';
  return 'web';
}

/**
 * La plataforma de ESTA sesión, leyendo las señales reales del entorno.
 * Best-effort: matchMedia puede no existir (jsdom, WebViews viejos) y un
 * fallo acá jamás puede romper la telemetría, así que cae a 'web'.
 */
export function plataformaActual() {
  try {
    return clasificarPlataforma({
      nativo: isNative,
      plataformaNativa: platform,
      displayModeStandalone:
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
          ? window.matchMedia('(display-mode: standalone)').matches
          : false,
      navigatorStandalone:
        typeof navigator !== 'undefined' ? navigator.standalone === true : false,
    });
  } catch {
    return 'web';
  }
}

// ─── Aparato para telemetría ──────────────────────────────────────────────────
//
// POR QUÉ EXISTE (2026-10-12). `plataforma` responde "¿quién se actualiza
// solo?" pero no "¿en qué aparato usan MiPAE?": un Safari de iPhone y un Chrome
// de escritorio caen los dos en `web`. El fundador pidió saber "si son de
// iPhone, Google, PC", y eso es esta columna. Catálogo CERRADO de cinco
// valores, PII-safe por construcción; `otro` existe para no inventar.
export const APARATOS = ['iphone', 'ipad', 'android', 'computador', 'otro'];

/**
 * Clasifica el aparato. Pura y testeable: recibe las señales, no las lee.
 *
 * Lo nativo manda primero (misma lección que `clasificarPlataforma`: el
 * WebView de la app se disfraza de navegador). Dentro de la app de iOS, el
 * iPad se distingue del iPhone por el user agent. Un iPad con Safari moderno
 * se presenta como "Macintosh" (modo escritorio), y la única pista que queda
 * es que tiene pantalla táctil: por eso `maxTouchPoints` entra en la decisión.
 */
export function clasificarAparato({
  nativo,
  plataformaNativa,
  ua = '',
  maxTouchPoints = 0,
} = {}) {
  const agente = String(ua || '');
  const esIpad = /iPad/i.test(agente) || (/Macintosh/i.test(agente) && Number(maxTouchPoints) > 1);
  if (nativo) {
    if (plataformaNativa === 'android') return 'android';
    if (plataformaNativa === 'ios') return esIpad ? 'ipad' : 'iphone';
  }
  if (/iPhone|iPod/i.test(agente)) return 'iphone';
  if (esIpad) return 'ipad';
  if (/Android/i.test(agente)) return 'android';
  if (/Windows|Macintosh|Linux|CrOS|X11/i.test(agente)) return 'computador';
  return 'otro';
}

/**
 * El aparato de ESTA sesión, leyendo las señales reales del entorno.
 * Best-effort: un fallo acá jamás rompe la telemetría, así que cae a 'otro'.
 */
export function aparatoActual() {
  try {
    return clasificarAparato({
      nativo: isNative,
      plataformaNativa: platform,
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      maxTouchPoints: typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0,
    });
  } catch {
    return 'otro';
  }
}

// ─── fetchCrudo: el fetch del navegador, saltándose el parche de CapacitorHttp ─
//
// capacitor.config.json tiene CapacitorHttp encendido, y eso REEMPLAZA
// window.fetch en la app instalada por una versión que pasa cada request por
// código nativo. Esa versión tiene tres huecos que se estrenaron el 2026-09-03
// en el primer iPhone real (build 2128):
//
//  1. No sabe transportar un Blob: su conversor de cuerpo tiene rama para
//     FormData, File, Uint8Array y string, pero no para Blob, así que el audio
//     del dictado cruzaba el puente como "{}" y ElevenLabs nunca recibió nada
//     (bug abierto ionic-team/capacitor#6132). Ese era el cartel rojo "No se
//     pudo transcribir lo que dijiste" de la primera tester.
//  2. Descarta el AbortSignal: ningún timeout ni el botón Detener de Benito
//     cancelan nada en nativo. El límite real pasa a ser el de Swift (600 s).
//  3. Bufferea la respuesta completa antes de devolverla: el streaming SSE de
//     Benito llega de golpe al final en vez de palabra a palabra.
//
// El propio parche guarda el fetch original en window.CapacitorWebFetch antes
// de reemplazarlo (native-bridge.js), y eso es lo que se usa aquí, atado a
// window porque llamarlo suelto tira "Illegal invocation". Solo lo usan las
// llamadas que el parche demostradamente rompe: el audio, el streaming y el
// PDF. El resto de la app sigue por el parche, que para JSON funciona y en
// Android lleva un mes en producción.
//
// Contrapartida que NO se puede olvidar: al saltarse el parche, la request sale
// con el Origin del WebView (capacitor://localhost en iOS, https://localhost en
// Android) y pasa por CORS de verdad. Cada endpoint que se llame por aquí tiene
// que tener esos dos orígenes en su allowlist (api/transcribe.js, api/claude.js,
// api/pdf/generate.js los tienen desde este mismo cambio). Llamar por fetchCrudo
// a un endpoint que no los tenga es un fallo garantizado en la app instalada.
//
// Kill-switch ON por defecto: con REACT_APP_FETCH_CRUDO_NATIVO_ENABLED=false
// todo vuelve a pasar por el parche (y el dictado de iPhone vuelve a morir).
const FETCH_CRUDO_ENABLED = process.env.REACT_APP_FETCH_CRUDO_NATIVO_ENABLED !== 'false';

export function fetchCrudo(recurso, opciones) {
  const w = typeof window !== 'undefined' ? window : undefined;
  const original = FETCH_CRUDO_ENABLED && w && typeof w.CapacitorWebFetch === 'function'
    ? w.CapacitorWebFetch
    : (w && w.fetch) || fetch;
  return original.call(w, recurso, opciones);
}
